import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

import { IoChatbubblesSharp } from "react-icons/io5";
import { MdOutlineArticle } from "react-icons/md";
import { IoPeopleSharp } from "react-icons/io5";
import { IoDocumentSharp } from "react-icons/io5";
import { IoDocuments } from "react-icons/io5";
import { IoLocationSharp } from "react-icons/io5";
import { IoWalletSharp } from "react-icons/io5";
import { IoRibbonSharp } from "react-icons/io5";
import { FaChalkboardTeacher } from "react-icons/fa";
import { IoMailSharp } from "react-icons/io5";
import { FaRectangleList } from "react-icons/fa6";
import { RiSurveyFill } from "react-icons/ri";
import { PiExam } from "react-icons/pi";
import { RiExpandRightLine } from "react-icons/ri";
import { BiSolidDiscount } from "react-icons/bi";
import { BsClipboard2DataFill } from "react-icons/bs";
import { VscDebugDisconnect } from "react-icons/vsc";
import { FaObjectGroup } from "react-icons/fa";
import { FaClipboardList } from "react-icons/fa";
import { IoStorefrontOutline, IoDocumentAttachOutline, IoNewspaperOutline } from "react-icons/io5";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import { IoDocumentTextSharp } from "react-icons/io5";
import { MdOutlineReceiptLong } from "react-icons/md";

import { SidebarItemOption } from "./SidebarItem";
import { SidebarItemGroup } from "./SidebarItemGroup";
import { brandLogoUrl } from "../branding";
import { routes } from "consts";
import { CURRENT_VERSION_NO } from "pages/Versions/VersionsPage";
import { useDocumentsV2Enabled } from "api/hooks/useDocumentsV2Enabled";

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (val: boolean) => void;
}

const menuItems: SidebarItemOption[] = [
  // {
  //   title: "Zadania",
  //   path: routes.TASKS,
  //   icon: (props) => <IoChatbubblesSharp {...props} />,
  // },
  {
    title: "Grupy kursów",
    path: routes.COURSE_GROUPS,
    icon: (props) => <FaObjectGroup {...props} />,
  },
  {
    title: "Kursy",
    path: routes.COURSES,
    icon: (props) => <MdOutlineArticle {...props} />,
  },
  // {
  //   title: "Rejestracje",
  //   path: routes.REGISTRATIONS,
  //   icon: (props) => <FaClipboardList {...props} />,
  // },
  {
    title: "Użytkownicy",
    path: routes.STUDENTS,
    icon: (props) => <IoPeopleSharp {...props} />,
  },
  {
    title: "Tematy",
    path: routes.TOPICS,
    icon: (props) => <IoDocumentSharp {...props} />,
  },
  {
    title: "Grupy tematów",
    path: routes.TOPICS_GROUPS,
    icon: (props) => <IoDocuments {...props} />,
  },
  {
    title: "Miejsca",
    path: routes.PLACES,
    icon: (props) => <IoLocationSharp {...props} />,
  },
  {
    title: "Finanse",
    path: routes.FINANCES,
    icon: (props) => <IoWalletSharp {...props} />,
  },
  {
    title: "Kody rabatowe",
    path: routes.DISCOUNTS,
    icon: (props) => <BiSolidDiscount {...props} />,
  },
  {
    title: "Trenerzy",
    path: routes.TRAINERS,
    icon: (props) => <FaChalkboardTeacher {...props} />,
  },
  {
    title: "Potwierdzenia obecności",
    path: routes.TRAINER_LECTURE_CONFIRMATIONS,
    icon: (props) => <IoCheckmarkCircleOutline {...props} />,
  },
  {
    title: "Słowniki",
    path: routes.DICTIONARIES,
    icon: (props) => <FaRectangleList {...props} />,
  },
  {
    title: "Dokumenty",
    path: routes.DOCUMENTS,
    icon: (props) => <IoRibbonSharp {...props} />,
  },
  {
    title: "Email i SMS",
    icon: (props) => <IoMailSharp {...props} />,
    path: routes.EMAILS + routes.SCHEDULED_EMAILS,
  },
  {
    title: "Ankiety",
    path: routes.USER_SURVEYS,
    icon: (props) => <RiSurveyFill {...props} />,
  },
  {
    title: "Egzaminy",
    path: routes.EXAMS,
    icon: (props) => <PiExam {...props} />,
  },
  {
    title: "Raporty",
    path: routes.REPORTS,
    icon: (props) => <BsClipboard2DataFill {...props} />,
  },
  {
    title: "KSeF",
    path: routes.KSEF,
    icon: (props) => <MdOutlineReceiptLong {...props} />,
  },
  {
    title: "Sklep",
    path: routes.SHOP_LANDING_PAGES,
    icon: (props) => <IoStorefrontOutline {...props} />,
  },
  {
    title: "Blog",
    path: routes.SHOP_BLOG,
    icon: (props) => <IoNewspaperOutline {...props} />,
  },
  {
    title: "Pliki statyczne",
    path: routes.STATIC_FILES,
    icon: (props) => <IoDocumentAttachOutline {...props} />,
  },
];

// Shown right after "Dokumenty", only for tenants that switched "Dokumenty 2.0" on in the settings.
const documentsV2MenuItem: SidebarItemOption = {
  title: "Dokumenty 2.0",
  path: routes.DOCUMENTS_V2,
  icon: (props) => <IoDocumentTextSharp {...props} />,
};

const withDocumentsV2Item = (items: SidebarItemOption[]): SidebarItemOption[] => {
  const documentsIndex = items.findIndex((item) => item.path === routes.DOCUMENTS);
  if (documentsIndex === -1) return items;
  return [...items.slice(0, documentsIndex + 1), documentsV2MenuItem, ...items.slice(documentsIndex + 1)];
};

export const Sidebar = ({ sidebarOpen, setSidebarOpen }: SidebarProps): JSX.Element => {
  const { enabled: documentsV2Enabled } = useDocumentsV2Enabled();
  const visibleMenuItems = useMemo(
    () => (documentsV2Enabled ? withDocumentsV2Item(menuItems) : menuItems),
    [documentsV2Enabled]
  );
  const trigger = useRef<HTMLButtonElement>(null);
  const sidebar = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // the mobile drawer covers the page, so close it once a menu item is picked (also the screen already open)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location]);

  const storedSidebarExpanded = localStorage.getItem("sidebar-expanded");
  const [sidebarExpanded, setSidebarExpanded] = useState(
    storedSidebarExpanded === null ? false : storedSidebarExpanded === "true",
  );

  // close on click outside
  useEffect(() => {
    const clickHandler: EventListener = ({ target }) => {
      if (!sidebar.current || !trigger.current) return;
      if (!sidebarOpen || sidebar.current.contains(target as Node) || trigger.current.contains(target as Node)) return;
      setSidebarOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: { keyCode: number }) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  useEffect(() => {
    localStorage.setItem("sidebar-expanded", sidebarExpanded.toString());
    if (sidebarExpanded) {
      document.querySelector("body")?.classList.add("sidebar-expanded");
    } else {
      document.querySelector("body")?.classList.remove("sidebar-expanded");
    }
  }, [sidebarExpanded]);

  return (
    <div className="min-w-fit">
      {/* Sidebar backdrop (mobile only) */}
      <div
        className={`fixed inset-0 z-40 bg-slate-900 bg-opacity-30 transition-opacity duration-200 lg:z-auto lg:hidden ${
          sidebarOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        aria-hidden="true"
      ></div>

      {/* Sidebar */}
      <div
        id="sidebar"
        ref={sidebar}
        className={`no-scrollbar absolute left-0 top-0 z-40 flex h-[100dvh] w-20 shrink-0 flex-col overflow-y-scroll border-r border-slate-700/50 bg-gradient-to-b from-slate-800 to-slate-900 shadow-xl transition-all duration-300 ease-in-out sidebar-expanded:w-64 max-lg:w-64 lg:static lg:left-auto lg:top-auto lg:translate-x-0 lg:overflow-y-auto ${
          sidebarOpen ? "translate-x-0" : "-translate-x-64"
        }`}
      >
        {/* Sidebar header */}
        <div className="mb-8 flex h-20 items-center justify-between px-4">
          {/* Logo */}
          <Link to="/" className="block shrink-0">
            <img src={brandLogoUrl} alt="Logo" className="h-8 w-8 rounded object-contain" />
          </Link>
          {/* Close button */}
          <button
            ref={trigger}
            className="rounded-lg p-2 text-slate-400 transition-colors duration-200 hover:bg-slate-700/50 hover:text-slate-300 lg:hidden"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-controls="sidebar"
            aria-expanded={sidebarOpen}
          >
            <span className="sr-only">Close sidebar</span>
            <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.7 18.7l1.4-1.4L7.8 13H20v-2H7.8l4.3-4.3-1.4-1.4L4 12z" />
            </svg>
          </button>
        </div>

        {/* Links */}
        <div className="flex-1 px-4">
          <ul className="space-y-1">
            {visibleMenuItems.map((menuItem: SidebarItemOption) => (
              <SidebarItemGroup key={menuItem.path} {...menuItem} />
            ))}
          </ul>
        </div>

        {/* Footer */}
        <div className="mt-auto border-t border-slate-700/50 px-4 py-4">
          <div className="flex items-center justify-between">
            <Link
              to={routes.VERSIONS}
              className="hidden items-center rounded-lg px-2 py-1 text-xs text-slate-400 transition-colors duration-200 hover:bg-slate-700/50 hover:text-slate-300 sidebar-expanded:flex max-lg:flex"
            >
              <span className="font-medium">v{CURRENT_VERSION_NO}</span>
            </Link>

            <button
              onClick={() => setSidebarExpanded(!sidebarExpanded)}
              className="group flex h-10 w-10 items-center max-lg:hidden justify-center rounded-lg bg-slate-700/50 text-slate-400 transition-all duration-200 hover:bg-slate-700 hover:text-slate-300 hover:shadow-lg"
              title={sidebarExpanded ? "Zwiń menu" : "Rozwiń menu"}
            >
              <RiExpandRightLine
                className={`transition-transform duration-300 ease-in-out ${
                  sidebarExpanded ? "rotate-180" : "rotate-0"
                } group-hover:scale-110`}
                size={20}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
